import { SharedData } from "@/types";
import { router, usePage } from "@inertiajs/react";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import NotificationCard from "./notification-card";

// Initialize dayjs with relativeTime plugin
dayjs.extend(relativeTime);

type NotificationListItem = Parameters<typeof NotificationCard>[0]["notification"];

interface DisplayAllNotificationsProps {
  className?: string;
  // The bell keeps the full history (read + unread) in local React state. We
  // read it here instead of relying solely on page props, because the
  // `notifications` prop is lazy on Inertia XHR navigations and is therefore
  // omitted (undefined) after the first full page load — which left this list
  // empty.
  notifications?: NotificationListItem[];
}

function sortByCreatedDesc(items: NotificationListItem[]): NotificationListItem[] {
  return [...items].toSorted(
    (a, b) => dayjs(b.createdAt ?? 0).valueOf() - dayjs(a.createdAt ?? 0).valueOf(),
  );
}

function DisplayAllNotifications({ className, notifications }: DisplayAllNotificationsProps) {
  const { t, i18n } = useTranslation("notifications");
  const { notifications: sharedNotifications } = usePage<SharedData>().props;
  const [searchQuery, setSearchQuery] = useState("");

  // Set dayjs locale based on i18n language
  const currentLocale = i18n.language;
  dayjs.locale(currentLocale || "en");

  // Prefer the live data passed down from the bell; fall back to page props
  // (only populated on a full page load or an explicit partial reload).
  const propNotifications: NotificationListItem[] =
    sharedNotifications &&
    "allNotifications" in sharedNotifications &&
    Array.isArray(sharedNotifications.allNotifications)
      ? sharedNotifications.allNotifications.map((notification) => ({
          id: notification.id,
          type: notification.type,
          data: {},
          createdAt: notification.created_at,
          readAt: notification.read_at,
          message: notification.message,
          url: notification.url,
          invitationToken: notification.invitationToken ?? null,
        }))
      : [];

  const source = notifications && notifications.length > 0 ? notifications : propNotifications;
  const allNotifications = sortByCreatedDesc(source);

  const filteredNotifications = allNotifications.filter((notification) =>
    String(notification.message ?? "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const handleOpenChange = (open: boolean) => {
    // The `notifications` prop is lazy on Inertia requests, so refresh it when
    // the sheet opens to pull the complete, up-to-date history (including
    // notifications read since the last full page load).
    if (open) {
      router.reload({ only: ["notifications"] });
    }
  };

  return (
    <Sheet onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <div className={className}>{t("dropdown.viewAll")}</div>
      </SheetTrigger>
      <SheetContent className="p-6">
        <SheetHeader className="pb-6">
          <SheetTitle>{t("title")}</SheetTitle>
          <SheetDescription>{t("empty.description")}</SheetDescription>
          <div className="relative mt-4">
            <Search className="text-muted-foreground absolute top-2.5 left-2 h-4 w-4" />
            <Input
              placeholder={t("search.placeholder")}
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-12rem)] pr-4">
          <div className="flex flex-col gap-2">
            {filteredNotifications.map((notification) => (
              <NotificationCard key={notification.id} notification={notification} />
            ))}
            {filteredNotifications.length === 0 && (
              <div className="text-muted-foreground py-8 text-center">
                {searchQuery ? t("search.noResults") : t("empty.state")}
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default DisplayAllNotifications;
